import querystring from 'querystring';

import fetch from 'isomorphic-fetch';

import { updateLoginIsFetching, updateLoginPlainError } from 'actions/login';
import { openModal } from 'actions/modals';
import {
  HEADER_CONTENT_TYPE,
  STATUS_CONFLICT,
  STATUS_FORBIDDEN,
  STATUS_UNAUTHORIZED,
} from 'constants/http';
import { JSON as JSON_MIME } from 'constants/mimes';
import { VERIFY_EMAIL_MODAL } from 'constants/modalTypes';
import * as routes from 'constants/routes';
import * as analytics from 'utilities/analytics';
import * as auth from 'utilities/auth';
import * as extension from 'utilities/extension';
import * as sentry from 'utilities/sentry';
import { store } from 'utilities/store';
import { getParam } from 'utilities/url';

import CheckIfUserWithEmailExists from './CheckIfUserWithEmailExists.graphql';
import isUnsafeRedirectUrl from '../utilities/isUnsafeRedirectUrl';
import { showErrorBar } from '../actions/errorBar';
import { postRecordReplyComment } from '../components/share-video/share-record-reply-signup-modal/utilities';
import { ERROR } from '../constants/error-severities';
import { EMAIL_LOGIN_FAILED, EMAIL_SIGNUP_FAILED } from '../constants/events';
import { getGraphQLClient } from '../utilities/graphql';
import {
  createAnonRecordingCache,
  updateAnonRecordingOwnership,
} from '../utilities/shareVideo';

const ACCOUNT_EXISTS_ERROR = 'You appear to already have an account.';
const ACCOUNT_IS_SSO =
  'This email belongs to a Company account. Speak with your administrator about provisioning a Loom account for this email.';

const LOGIN_INCORRECT_ERROR =
  'Your login information was incorrect. Please ' + 'check and try again.';

const SERVER_ERROR =
  'Oops! Something went wrong on our end. ' + 'Please try that again.';

export function logInUserCreator(email, password, redirectURL) {
  return dispatch => {
    dispatch(updateLoginIsFetching(true));

    auth
      .login(email, password)
      .done(async () => {
        // for users signing in from the anonymous record reply signup modal,
        // post the video if it exists
        const { modals, video_player } = store.getState();
        const recordedReplyVideoId = modals?.options?.videoId;
        const addReplyId = modals?.options?.addReplyId;
        const videoCurrentTime = video_player?.current_time;
        const repliedToVideoId = video_player?.video?.id;

        if (recordedReplyVideoId) {
          await createAnonRecordingCache({
            replyVideoId: recordedReplyVideoId,
            repliedToVideoId,
            videoCurrentTime,
            addReplyId,
          });

          postRecordReplyComment({
            recordedReplyVideoId,
            addReplyId,
            videoCurrentTime,
          });

          await updateAnonRecordingOwnership();
        }

        dispatch(updateLoginIsFetching(false));
        extension.signalExtensionLogin(() => {
          let cameFrom = getParam('redirect_after') || redirectURL;

          if (cameFrom) {
            if (isUnsafeRedirectUrl(cameFrom)) {
              cameFrom = '/';
            }

            // if they came from somewhere, send them back there
            window.location.href = cameFrom;
          } else {
            window.location.href = routes.ROOT_PAGE;
          }
        });
      })
      .fail(xhr => {
        dispatch(updateLoginIsFetching(false));

        if (xhr.status === STATUS_UNAUTHORIZED) {
          analytics.track(EMAIL_LOGIN_FAILED, {
            reason: 'incorrect_login',
          });

          if (xhr.responseText.length > 0) {
            dispatch(updateLoginPlainError(xhr.responseText));
          } else {
            dispatch(updateLoginPlainError(LOGIN_INCORRECT_ERROR));
          }
        } else if (xhr.status === STATUS_FORBIDDEN) {
          analytics.track(EMAIL_LOGIN_FAILED, {
            reason: 'account_must_sso',
          });

          if (xhr.responseText.length > 0) {
            dispatch(updateLoginPlainError(xhr.responseText));
          } else {
            dispatch(updateLoginPlainError(ACCOUNT_IS_SSO));
          }
        } else {
          analytics.track(EMAIL_LOGIN_FAILED, {
            reason: 'unknown',
          });
          dispatch(updateLoginPlainError(SERVER_ERROR));
        }
      });
  };
}

export function checkIfUserExists(email, { errorIfExists = true } = {}) {
  return async dispatch => {
    const gqlClient = getGraphQLClient();

    try {
      const { data } = await gqlClient.mutate({
        mutation: CheckIfUserWithEmailExists,
        variables: {
          email,
        },
      });
      const exists = data.checkIfUserWithEmailExists;

      if (exists && errorIfExists) {
        analytics.track(EMAIL_SIGNUP_FAILED, {
          reason: 'account_exists',
        });
        dispatch(updateLoginPlainError(ACCOUNT_EXISTS_ERROR));
      }

      return exists;
    } catch (err) {
      dispatch(showErrorBar(ERROR, 'Oops! Something went wrong'));

      sentry.logException(err, 'Error while checking for user existence');
    }
  };
}

export function signUpUserCreator({
  email,
  password,
  firstName,
  lastName = null,
  modalMode,
  redirectURL = null,
  termsAccepted = false,
  callback,
  inviteLinkId,
  queryParams = null,
}) {
  return dispatch => {
    dispatch(updateLoginIsFetching(true));

    analytics.ready(() => {
      const { anonID, deviceID } = analytics.getAnalyticsIds();

      const body = JSON.stringify({
        anonID,
        deviceID,
        email,
        password,
        firstName,
        lastName,
        termsAccepted,
      });

      let params = window.location.search;

      let cameFrom = getParam('redirect_after');

      if (isUnsafeRedirectUrl(cameFrom)) {
        cameFrom = '/';
      }

      if (queryParams) {
        const additionalQueryParamString = querystring.stringify(queryParams);

        params = params
          ? `${params}&${additionalQueryParamString}`
          : `?${additionalQueryParamString}`;
      }

      fetch(`/v1/users${params}`, {
        method: 'POST',
        credentials: 'include',
        headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
        body,
      })
        .then(resp => {
          if (resp.status === STATUS_CONFLICT) {
            analytics.track(EMAIL_SIGNUP_FAILED, {
              reason: 'account_exists',
            });
            dispatch(updateLoginPlainError(ACCOUNT_EXISTS_ERROR));
            dispatch(updateLoginIsFetching(false));

            return false;
          }

          if (resp.status === STATUS_FORBIDDEN) {
            analytics.track(EMAIL_SIGNUP_FAILED, {
              reason: 'account_must_sso',
            });
            dispatch(updateLoginPlainError(ACCOUNT_IS_SSO));
            dispatch(updateLoginIsFetching(false));

            return false;
          }

          if (resp.status >= 400) {
            analytics.track(EMAIL_SIGNUP_FAILED, {
              reason: 'unknown',
            });
            throw new Error(`POST /v1/users - HTTP response: ${resp.status}`);
          }

          return true;
        })
        .then(result => {
          dispatch(updateLoginIsFetching(false));

          if (callback) {
            callback();

            return;
          }

          if (modalMode) {
            dispatch(
              openModal(VERIFY_EMAIL_MODAL, { modalMode, email, inviteLinkId })
            );

            return;
          }

          // do not redirect in case of error/failure
          if (!result) {
            return;
          }

          extension.signalExtensionLogin(() => {
            window.location.href = decodeURIComponent(
              cameFrom ||
                redirectURL ||
                `${routes.WELCOME}${window.location.search}`
            );
          });
        })
        .catch(err => {
          analytics.track(EMAIL_SIGNUP_FAILED, {
            reason: 'unknown',
          });
          dispatch(updateLoginIsFetching(false));
          dispatch(updateLoginPlainError(SERVER_ERROR));
          sentry.logException(err, 'Error in signUpUserCreator');
        });
    });
  };
}
